import type { lifetimeConfigParams, spatialTypeOfMovement } from '@hub/bearing-hub/common/constants';
import type {
  IbhLifetimePartLinearAndPivotingRequestDto,
  IbhLifetimePartLinearAndRotationRequestDto,
  IbhLifetimePartLinearRequestDto,
  IbhLifetimePartPivotingRequestDto,
  IbhLifetimePartRotationRequestDto,
  IbhSpatialTypeOfMovements,
  IbhTechnicalEnvironment,
  IbhTemporalTypeOfMovement,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';

export type IbhLifeTimeConfig =
  | IbhLifetimePartLinearRequestDto
  | IbhLifetimePartPivotingRequestDto
  | IbhLifetimePartRotationRequestDto
  | IbhLifetimePartLinearAndPivotingRequestDto
  | IbhLifetimePartLinearAndRotationRequestDto;

export type LifetimeConfigParams = (typeof lifetimeConfigParams)[keyof typeof lifetimeConfigParams];

export type LifetimeConfig = {
  [spatialTypeOfMovement.linear]?: LinearLifetimeConfig;
  [spatialTypeOfMovement.pivoting]?: PivotingLifetimeConfig;
  [spatialTypeOfMovement.rotation]?: RotationLifetimeConfig;
  [spatialTypeOfMovement.linearAndPivoting]?: LinearAndPivotingLifetimeConfig;
  [spatialTypeOfMovement.linearAndRotation]?: LinearAndRotationLifetimeConfig;
};

export const temporalTypeOfMovement = {
  simultaneous: 'simultaneous',
  sequential: 'sequential',
} as const;

export const lifetimeConfigTypeOfOperation = {
  continous: 'continous',
  intermittent: 'intermittent',
} as const;

export type LifetimeConfigTypeOfOperation =
  (typeof lifetimeConfigTypeOfOperation)[keyof typeof lifetimeConfigTypeOfOperation];

export type IntermittentParams = {
  offTime: number;
  onTime: number;
};

export type Load = {
  force: number;
};

export type TemporalTypeOfMovement = (typeof temporalTypeOfMovement)[keyof typeof temporalTypeOfMovement];

export type BaseLifetimeConfig = {
  [lifetimeConfigParams.acceptableWear]?: number;
  [lifetimeConfigParams.activityMinutesPerHour]?: number;
  [lifetimeConfigParams.environment]?: IbhTechnicalEnvironment;
  [lifetimeConfigParams.typeOfOperation]?: LifetimeConfigTypeOfOperation;
  [lifetimeConfigParams.intermittentParams]?: IntermittentParams;
  [lifetimeConfigParams.onTime]?: number;
  [lifetimeConfigParams.offTime]?: number;
  [lifetimeConfigParams.load]?: Load;
  [lifetimeConfigParams.surfacePressure]?: number;
  [lifetimeConfigParams.force]?: number;
  [lifetimeConfigParams.oppositeMaterialId]?: string;
  [lifetimeConfigParams.temperature]?: number;
  [lifetimeConfigParams.typeOfMovement]?: IbhSpatialTypeOfMovements;
};

export type LinearLifetimeConfig = {
  [lifetimeConfigParams.linearFrequency]?: number;
  [lifetimeConfigParams.linearStrokeLength]?: number;
} & BaseLifetimeConfig;

export type PivotingLifetimeConfig = {
  [lifetimeConfigParams.pivotingAngle]?: number;
  [lifetimeConfigParams.pivotingFrequency]?: number;
} & BaseLifetimeConfig;

export type RotationLifetimeConfig = {
  [lifetimeConfigParams.rotationRevolutions]?: number;
} & BaseLifetimeConfig;

export type LinearAndPivotingLifetimeConfig = {
  [lifetimeConfigParams.linearFrequency]?: number;
  [lifetimeConfigParams.linearStrokeLength]?: number;
  [lifetimeConfigParams.pivotingAngle]?: number;
  [lifetimeConfigParams.pivotingFrequency]?: number;
  temporalTypeOfMovement?: IbhTemporalTypeOfMovement;
  temporalLinearPart?: number;
} & BaseLifetimeConfig;

export type LinearAndRotationLifetimeConfig = {
  [lifetimeConfigParams.linearFrequency]?: number;
  [lifetimeConfigParams.linearStrokeLength]?: number;
  [lifetimeConfigParams.rotationRevolutions]?: number;
  temporalTypeOfMovement?: IbhTemporalTypeOfMovement;
  temporalLinearPart?: number;
} & BaseLifetimeConfig;

export type LifetimeParameters = BaseLifetimeConfig &
  LinearLifetimeConfig &
  PivotingLifetimeConfig &
  RotationLifetimeConfig &
  LinearAndPivotingLifetimeConfig &
  LinearAndRotationLifetimeConfig;
